// The Findings page's cases over a span, read as the page reads them
// (pages/TriagePage.jsx, then components/FindingsConsole.jsx), for the
// Overview to count. What the page lists is what the Overview counts, so a
// change to either read belongs in both.
import { useEffect, useMemo, useState } from "react";
import { useInboxCases } from "./CaseInbox.jsx";
import { useTriageReads } from "./lenses/ReviewsBody.jsx";
import { consoleEntries } from "../lib/findingsConsole.js";
import { caseView } from "../lib/investigateCases.js";
import { listOpenCaseFindingIds } from "../lib/useConsoleInvestigations.js";

const NO_SCOPE = [];
const lower = (v) => String(v ?? "").trim().toLowerCase();

/** A finding review cleared, as a case of its own (the case it is once reopened). */
export const clearedCaseOf = (p) => caseView({ id: `r:${lower(p.finding.id)}`, title: "", notes: [], related: [], matches: [] }, [p]);

// The findings of open investigations, read once a visit: null while read.
export function useOpenCaseFindings(enabled) {
    const [ids, setIds] = useState(null);
    useEffect(() => {
        if (!enabled) return undefined;
        const ac = new AbortController();
        listOpenCaseFindingIds({ signal: ac.signal }).then(setIds, (err) => {
            if (ac.signal.aborted) return;
            console.warn("Findings could not read the open investigations; cases triage no longer escalates may be missing.", err);
            setIds([]);
        });
        return () => ac.abort();
    }, [enabled]);
    return ids;
}

/**
 * The organization's cases in `range` (an API range, lib/shell.js toApiRange
 * or apiSpan), each with where the team left it, from the investigations
 * `investigations` holds (useConsoleInvestigations). `keepIds`: the findings
 * of open investigations (useOpenCaseFindings); while null, nothing is read,
 * so a caller holds a read back by passing null. The read is what triage
 * escalated, paged up to its ceiling (lib/scopeReviews.js ESCALATED_CEILING),
 * as on the Findings page. `entries` is null while read; `clipped` says only
 * the newest findings were read.
 */
export function useFindingCases({ range, caps, keepIds, investigations }) {
    const escalated = useMemo(() => ({ keepIds }), [keepIds]);
    const t = useTriageReads({ scopePath: NO_SCOPE, range, caps, publish: false, followRuns: false, escalated });
    const { cases, placed } = useInboxCases(t, { rare: false, keep: keepIds });
    const ready = investigations.status === "done";
    const entries = useMemo(() => (cases && ready ? consoleEntries({ cases, placed, byId: investigations.byId, caseOf: clearedCaseOf }) : null), [cases, placed, ready, investigations.byId]);
    const status = t.failed || investigations.status === "error" ? "failed" : entries ? "ready" : "loading";
    return { status, entries, clipped: !!t.conductState?.clipped };
}
